import { Grid, Stack, Typography } from '@mui/material'
import type { FC } from 'react'
import { useEffect, useState } from 'react'

import type { DateFilterOption } from '@/components/DateFilters'
import DateFilters from '@/components/DateFilters'
import { getFilterDateRange } from '@/components/DateFilters/utils'
import DateTotalsGraph from '@/components/DateTotalsGraph'
import DownloadCSV from '@/components/DownloadCSV'
import TotalBox from '@/components/TotalBox'
import PaperedGridItem from '@/components/UI/PaperedGridItem'
import useUser from '@/hooks/useUser'
import {
  getActivitiesForUserQuery,
  getTotalActivitiesByDateForUserQuery,
} from '@/lib/apollo'
import { getTimeInterval } from '@/utils/dateRange'
import { genericErrorMessage } from '@/utils/error'
import { defaultGridSpacing } from '@/utils/themes'

import ActivityTable from './ActivityTable'
import { rowsPerPage } from './ActivityTable/utils'
import {
  toDateFilterPayload,
  addEmptyDatestoTotalActivitiesData,
  getActivitiesForUserCsv,
  toDateTotalsGraphDataPoint,
} from './utils'

export type Props = {
  userId: string
  testId: string
  email: string
}

const ActivityTab: FC<Props> = ({ testId, userId, email }) => {
  const { customerId, token, accessKey } = useUser()

  const [selectedDateFilter, setSelectedDateFilter] = useState<DateFilterOption>('today')

  const [
    fetchTotalActivitesByDateForUser,
    {
      data: totalActivitiesData,
      loading: totalActivitiesLoading,
      error: totalActivitiesError,
    },
  ] = getTotalActivitiesByDateForUserQuery.useLazyQuery()

  const transformedTotalActivitiesData =
    getTotalActivitiesByDateForUserQuery.toTransformedData(totalActivitiesData)

  const [
    fetchActivities,
    { data: activitiesData, loading: activitiesLoading, error: activitiesError },
  ] = getActivitiesForUserQuery.useLazyQuery()

  const transformedActivitiesData =
    getActivitiesForUserQuery.toTransformedData(activitiesData)

  const [page, setPage] = useState<number>(0)

  const timeInterval = getTimeInterval(selectedDateFilter)

  useEffect(() => {
    if (!customerId) {
      return
    }

    const dateFilters = toDateFilterPayload(getFilterDateRange(selectedDateFilter))

    fetchTotalActivitesByDateForUser({
      variables: {
        customerId,
        userId,
        timeInterval,
        ...dateFilters,
      },
    })
  }, [
    fetchTotalActivitesByDateForUser,
    selectedDateFilter,
    timeInterval,
    customerId,
    userId,
  ])

  useEffect(() => {
    if (!customerId) {
      return
    }

    const dateFilters = toDateFilterPayload(getFilterDateRange(selectedDateFilter))

    fetchActivities({
      variables: {
        customerId,
        userId,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...dateFilters,
      },
    })
  }, [fetchActivities, page, selectedDateFilter, customerId, userId])

  const totalActivityGraphData = addEmptyDatestoTotalActivitiesData({
    data: transformedTotalActivitiesData,
    timeInterval,
    ...getFilterDateRange(selectedDateFilter),
  })

  const selectedDates = getFilterDateRange(selectedDateFilter)

  return (
    <Grid container spacing={defaultGridSpacing} data-testid={testId}>
      <Grid item container xs={12} direction="column" alignItems="flex-end">
        <DateFilters
          selected={selectedDateFilter}
          onChangeSelected={setSelectedDateFilter}
        />
      </Grid>
      <PaperedGridItem xs={4}>
        <Typography component="h2" variant="h6" gutterBottom>
          Total activities
        </Typography>
        <TotalBox
          loading={activitiesLoading || transformedActivitiesData === undefined}
          errorMessage={activitiesError ? genericErrorMessage : undefined}
          total={transformedActivitiesData?.total}
        />
      </PaperedGridItem>
      <PaperedGridItem xs={8}>
        <Typography component="h2" variant="h6" gutterBottom>
          Total activity
        </Typography>
        <DateTotalsGraph
          data={totalActivityGraphData.map(toDateTotalsGraphDataPoint)}
          loading={totalActivitiesLoading || transformedTotalActivitiesData === undefined}
          errorMessage={totalActivitiesError ? genericErrorMessage : undefined}
          timeInterval={timeInterval}
        />
      </PaperedGridItem>
      <PaperedGridItem xs={12}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography component="h2" variant="h6" gutterBottom>
            Browse activity
          </Typography>
          <DownloadCSV
            handleDownload={() =>
              getActivitiesForUserCsv({
                customerId,
                userId,
                email,
                startDate: selectedDates.startDate,
                endDate: selectedDates.endDate,
                token,
                accessKey,
              })
            }
          />
        </Stack>
        <ActivityTable
          errorMessage={activitiesError ? genericErrorMessage : undefined}
          loading={activitiesLoading}
          data={transformedActivitiesData}
          page={page}
          onChangePage={setPage}
        />
      </PaperedGridItem>
    </Grid>
  )
}

export default ActivityTab
